// src/screens/admin/MillasPanel.jsx
// Pestaña "Orçamento" del Panel de Administrador: calcula cuánto te cuesta un vuelo
// (con millas, o con el precio de Comprarviagem, Speedmilhas o un resumen de pedido),
// el precio al cliente con tu margen o el que tú escribas, y genera el orçamento
// (imagen o PDF) con la marca Click Travel Agency. Un pasajero por ahora.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from '../../components/UI';
import { fileToBase64Jpeg } from '../../services/imageGenService';
import { listarPolitica, guardarEntradaPolitica, borrarEntradaPolitica, migrarPolitica } from '../../services/milhasService';
import {
  AEROLINEAS, MARGENES, PASO_MILHEIRO, idAerolinea, ajustarMilheiro, valoresMilheiro,
  calcularMilhas, fmtBRL, CIUDADES, FUENTES, FACTOR_COMPRARVIAGEM, costoDeFuente, calcularPrecio, normalizarDuracion,
  totalesOrcamento, nochesEntre,
} from '../../services/milhasCalc';
import { tx } from '../../services/idiomaArte';
import { getAjustes } from '../../services/cotizadorService';
import ExportarArte, { CSS_ARTE as CSS, TIPOS, TIPO_POR_FORMATO, entregarArchivo, Interruptor } from './ExportarArte';
import { renderOrcamento, canvasToBlob, canvasToPdfBlob, FORMATOS } from '../../services/orcamentoRender';
import { NumInput, Campo, inputStyle, labelStyle } from './CotizadorUI';

const EXTRACT_URL = '/.netlify/functions/extract-flight';

// Textos sugeridos para Observaciones. Son solo los neutros de la herramienta de
// referencia. Las reglas de cada compañía (cambios, cancelación…) NO van aquí hasta
// que estén verificadas en fuentes oficiales y las apruebes.
const NOTAS = [
  { id: 'sujeitos', es: 'Valores sujetos a cambio hasta la emisión', def: true, pt: 'Valores sujeitos a alteração até a emissão.', textoEs: 'Valores sujetos a cambio hasta la emisión.' },
  { id: 'bagagem', es: 'Equipaje según la compañía', def: false, pt: 'Bagagem despachada conforme as regras da companhia aérea.', textoEs: 'Equipaje despachado según las reglas de la aerolínea.' },
  { id: 'docs', es: 'Documentos y visas por cuenta del cliente', def: false, pt: 'Documentação e vistos por conta do cliente.', textoEs: 'Documentación y visas por cuenta del cliente.' },
];
const LIMITE_OBS = 600;
const BAGAGEM = { pt: 'Mochila + mão 12 kg (conforme a companhia)', es: 'Mochila + mano 12 kg (según la aerolínea)' };

const PAGOS = [
  { id: 'PIX', es: 'PIX', sub: 'Al contado, instantáneo' },
  { id: 'Dinheiro', es: 'Efectivo', sub: 'Presencial en la agencia' },
  { id: 'Boleto', es: 'Boleto', sub: 'Se acredita en 1 a 3 días' },
  { id: 'Cartão de crédito', es: 'Tarjeta de crédito', sub: 'Hasta 12 cuotas' },
  { id: 'Faturado', es: 'Facturado', sub: 'Factura con vencimiento acordado' },
];

const PASOS_TODOS = [
  { id: 'vuelo', label: 'Vuelo' }, { id: 'valor', label: 'Valor' }, { id: 'items', label: 'Hotel y servicios' },
  { id: 'cliente', label: 'Cliente' }, { id: 'obs', label: 'Observaciones' }, { id: 'pago', label: 'Pago' }, { id: 'envio', label: 'Envío' },
];
const TIPOS_ITEM = [
  { id: 'hotel', label: 'Hotel' }, { id: 'servicio', label: 'Servicio' }, { id: 'omnibus', label: 'Ómnibus' },
];
const DIAS_VALIDEZ = [1, 3, 5, 7];

const LS = { rangos: 'click_milhas_rangos', politica: 'click_milhas_politica', textosViejos: 'click_milhas_textos' };

// Solo títulos de partida: el contenido de cada regla lo escribe el admin (son sus reglas, no las mías).
const TITULOS_POLITICA = ['Cancelación', 'Cambios de fecha', 'Plazo de devolución', 'Corrección de nombre', 'Emisión con millas de terceros'];
const leer = (k, def) => { try { const v = JSON.parse(localStorage.getItem(k)); return v ?? def; } catch { return def; } };
const guardar = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* sin almacenamiento */ } };

// "Mi política": reglas propias del admin con sus clientes, guardadas en este dispositivo.
// Si había textos guardados de la versión anterior, se convierten en entradas.
function cargarPolitica() {
  const p = leer(LS.politica, null);
  if (Array.isArray(p)) return p;
  const viejos = leer(LS.textosViejos, []);
  const migrados = Array.isArray(viejos)
    ? viejos.filter((t) => typeof t === 'string' && t.trim()).map((t, i) => ({ id: `m${Date.now()}${i}`, titulo: t.slice(0, 28), texto: t }))
    : [];
  if (migrados.length) guardar(LS.politica, migrados);
  return migrados;
}

const hoyMas = (dias) => {
  const d = new Date(); d.setDate(d.getDate() + dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const INICIAL = () => ({
  aerolinea: '', cabina: 'Econômica', origenCodigo: '', origenCiudad: '', destinoCodigo: '', destinoCiudad: '', destinoPais: 'Brasil',
  fechaIda: '', salida: '', llegada: '', duracion: '', conexiones: '',
  fuente: 'millas', precios: { comprarviagem: '', speedmilhas: '', resumen: '' }, precioLeido: null, precioManual: '',
  millas: '', taxa: '', milheiro: '', margen: 10, margenUlt: 10, margenOtro: '',
  sinVuelo: false, items: [], mostrarPrecioLinea: true,
  cliente: '', ref: '', numeroVuelo: '', validoHasta: hoyMas(3), pagos: ['PIX'], parcelas: 1,
  bagagem: BAGAGEM.pt,
});

// ── Íconos (SVG, sin emojis) ─────────────────────────────────────────
const Svg = ({ children, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const IcCamara = () => <Svg size={26}><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></Svg>;
const IcMenos = () => <Svg><path d="M6 12h12" /></Svg>;
const IcMas = () => <Svg><path d="M12 6v12M6 12h12" /></Svg>;
const IcLapiz = () => <Svg size={18}><path d="M4 20h4L19 9l-4-4L4 16z" /><path d="M13.5 6.5l4 4" /></Svg>;
const IcX = () => <Svg size={16}><path d="M6 6l12 12M18 6 6 18" /></Svg>;


const Fila = ({ children, cols = 2 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 10, marginBottom: 10 }}>{children}</div>
);

const Texto = ({ value, onChange, placeholder, type = 'text', maxLength, upper, ...rest }) => (
  <input
    type={type} value={value} placeholder={placeholder} maxLength={maxLength} {...rest}
    style={{ ...inputStyle, minHeight: 44, textTransform: upper ? 'uppercase' : 'none' }}
    onChange={(e) => onChange(upper ? e.target.value.toUpperCase() : e.target.value)}
  />
);

const etiquetaItem = (it) => (
  it.tipo === 'hotel' ? (it.nombre.trim() || 'Hotel')
    : it.tipo === 'omnibus' ? `Ómnibus ${[it.origen.trim(), it.destino.trim()].filter(Boolean).join(' → ')}`.trim()
      : (it.titulo.trim() || 'Servicio')
);

// Tarjeta de un hotel / servicio / ómnibus: su detalle (lo que ve el cliente) y su costo y precio (solo tú).
function TarjetaItem({ it, linea, margenPct, onChange, onQuitar }) {
  const costo = Number(it.costo);
  const sug = costo > 0 ? calcularPrecio({ costo, margenPct }) : null;
  const noches = it.tipo === 'hotel' ? nochesEntre(it.entrada, it.salida) : 0;
  const tipo = TIPOS_ITEM.find((t) => t.id === it.tipo);
  return (
    <div style={{ border: '1px solid var(--navy-border)', borderRadius: 16, padding: 14, marginBottom: 12, background: 'var(--navy-card)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <h4 style={{ fontSize: 14, fontWeight: 800, color: 'var(--orange)', margin: 0, letterSpacing: 0.5 }}>{tipo.label.toUpperCase()}</h4>
        <button type="button" aria-label={`Quitar ${etiquetaItem(it)}`} onClick={() => { if (window.confirm(`¿Quitar "${etiquetaItem(it)}" del orçamento?`)) onQuitar(); }}
          style={{ width: 44, height: 44, borderRadius: 12, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><IcX /></button>
      </div>

      {it.tipo === 'hotel' && (
        <>
          <Campo label="Nombre del hotel" style={{ marginBottom: 10 }}><Texto value={it.nombre} onChange={(v) => onChange({ nombre: v })} placeholder="Hotel Ibis Centro" /></Campo>
          <Fila cols={2}>
            <Campo label="Ciudad"><Texto value={it.ciudad} onChange={(v) => onChange({ ciudad: v })} placeholder="Florianópolis" /></Campo>
            <Campo label="Régimen (opcional)"><Texto value={it.regimen} onChange={(v) => onChange({ regimen: v })} placeholder="Café da manhã" /></Campo>
          </Fila>
          <Fila cols={2}>
            <Campo label="Entrada"><Texto type="date" value={it.entrada} onChange={(v) => onChange({ entrada: v })} /></Campo>
            <Campo label="Salida"><Texto type="date" value={it.salida} onChange={(v) => onChange({ salida: v })} /></Campo>
          </Fila>
          <Fila cols={2}>
            <Campo label="Huéspedes"><Texto type="number" inputMode="numeric" min={1} value={it.huespedes} onChange={(v) => onChange({ huespedes: v })} /></Campo>
            <Campo label="Habitaciones"><Texto type="number" inputMode="numeric" min={1} value={it.habitaciones} onChange={(v) => onChange({ habitaciones: v })} /></Campo>
          </Fila>
          {noches > 0 && <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 10px' }}>{noches} {noches === 1 ? 'noche' : 'noches'}</p>}
        </>
      )}

      {it.tipo === 'servicio' && (
        <>
          <Campo label="Servicio" style={{ marginBottom: 10 }}><Texto value={it.titulo} onChange={(v) => onChange({ titulo: v })} placeholder="Traslado aeropuerto → hotel" /></Campo>
          <Campo label="Detalle (opcional)" style={{ marginBottom: 10 }}><Texto value={it.detalle} onChange={(v) => onChange({ detalle: v })} placeholder="Ida y vuelta, carro particular" /></Campo>
        </>
      )}

      {it.tipo === 'omnibus' && (
        <>
          <Fila cols={2}>
            <Campo label="Origen"><Texto value={it.origen} onChange={(v) => onChange({ origen: v })} placeholder="Florianópolis" /></Campo>
            <Campo label="Destino"><Texto value={it.destino} onChange={(v) => onChange({ destino: v })} placeholder="Porto Alegre" /></Campo>
          </Fila>
          <Fila cols={3}>
            <Campo label="Fecha"><Texto type="date" value={it.fecha} onChange={(v) => onChange({ fecha: v })} /></Campo>
            <Campo label="Sale"><Texto type="time" value={it.sale} onChange={(v) => onChange({ sale: v })} /></Campo>
            <Campo label="Llega"><Texto type="time" value={it.llega} onChange={(v) => onChange({ llega: v })} /></Campo>
          </Fila>
          <Campo label="Empresa (opcional)" style={{ marginBottom: 10 }}><Texto value={it.empresa} onChange={(v) => onChange({ empresa: v })} placeholder="Eucatur" /></Campo>
        </>
      )}

      <div style={{ borderTop: '1px solid var(--navy-border)', paddingTop: 12 }}>
        <Fila cols={2}>
          <Campo label="Tu costo (R$)"><NumInput value={it.costo} onChange={(v) => onChange({ costo: v })} placeholder="0,00" /></Campo>
          <Campo label="Precio al cliente (R$)"><NumInput value={it.precioManual} onChange={(v) => onChange({ precioManual: v })} placeholder={sug ? fmtBRL(sug.precio) : 'sugerido'} /></Campo>
        </Fila>
        {linea?.completa && (
          <p style={{ fontSize: 12, margin: 0, color: linea.ganancia < 0 ? 'var(--error)' : 'var(--success)' }}>
            Precio R$ {fmtBRL(linea.precio)} · {Number(it.costo) > 0 ? (linea.ganancia < 0 ? `pierdes R$ ${fmtBRL(-linea.ganancia)}` : `ganas R$ ${fmtBRL(linea.ganancia)}`) : 'sin costo: cuenta como ganancia'}
          </p>
        )}
      </div>
    </div>
  );
}

// Resumen interno de todas las líneas con costo, precio y ganancia (esto no sale en la imagen).
function ResumenTotales({ totales, f }) {
  const filas = [];
  if (!f.sinVuelo) filas.push(`Vuelo ${f.origenCodigo || ''} → ${f.destinoCodigo || ''}`.replace(/\s+→\s*$/, '').trim());
  f.items.forEach((it) => filas.push(etiquetaItem(it)));
  const celda = { padding: '6px 0', fontSize: 13 };
  return (
    <div style={{ border: '1px solid var(--navy-border)', borderRadius: 16, overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', background: 'linear-gradient(135deg,#06284A,#0B4C8C)', color: '#fff' }}>
        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, opacity: 0.75, margin: 0 }}>TOTAL AL CLIENTE</p>
        <p style={{ fontSize: 30, fontWeight: 800, margin: '2px 0 0', lineHeight: 1.1 }}><span style={{ fontSize: 16, color: '#FF9A4D', marginRight: 6 }}>R$</span>{fmtBRL(totales.precio)}</p>
      </div>
      <div style={{ padding: '8px 14px', background: 'var(--navy-card)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '0 14px', alignItems: 'baseline' }}>
          <span style={{ ...celda, fontSize: 10, fontWeight: 700, color: 'var(--muted)' }}>LÍNEA</span>
          <span style={{ ...celda, fontSize: 10, fontWeight: 700, color: 'var(--muted)', textAlign: 'right' }}>TU COSTO</span>
          <span style={{ ...celda, fontSize: 10, fontWeight: 700, color: 'var(--muted)', textAlign: 'right' }}>PRECIO</span>
          {totales.lineas.map((l, i) => (
            <React.Fragment key={l.id}>
              <span style={{ ...celda, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{filas[i]}</span>
              <span style={{ ...celda, textAlign: 'right', color: 'var(--muted)' }}>{l.completa && l.costo > 0 ? fmtBRL(l.costo) : '—'}</span>
              <span style={{ ...celda, textAlign: 'right', color: l.completa ? 'var(--white)' : 'var(--gold)', fontWeight: 700 }}>{l.completa ? fmtBRL(l.precio) : 'falta'}</span>
            </React.Fragment>
          ))}
        </div>
        <div style={{ borderTop: '1px solid var(--navy-border)', marginTop: 6, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
          <span style={{ color: 'var(--muted)' }}>Tu costo total R$ {fmtBRL(totales.costo)}</span>
          <span style={{ fontWeight: 800, color: totales.ganancia < 0 ? 'var(--error)' : 'var(--success)' }}>
            {totales.ganancia < 0 ? `Pierdes R$ ${fmtBRL(-totales.ganancia)}` : `Ganas R$ ${fmtBRL(totales.ganancia)} (${fmtBRL(totales.gananciaPct, 1)}%)`}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function MillasPanel() {
  const [f, setF] = useState(INICIAL);
  const [rangos, setRangos] = useState(() => leer(LS.rangos, {}));
  const [editRango, setEditRango] = useState(false);
  const [notasSel, setNotasSel] = useState(() => new Set(NOTAS.filter((n) => n.def).map((n) => n.id)));
  const [politica, setPolitica] = useState(cargarPolitica);
  const [polDb, setPolDb] = useState(null); // null = cargando · true = en la nube · false = solo en este dispositivo
  const [politicaSel, setPoliticaSel] = useState(() => new Set());
  const [editando, setEditando] = useState(null); // { id, titulo, texto } o null
  const [factor, setFactor] = useState(FACTOR_COMPRARVIAGEM);
  useEffect(() => {
    let vivo = true;
    getAjustes().then((a) => { if (vivo && Number(a?.factor_comprarviagem) > 0) setFactor(Number(a.factor_comprarviagem)); }).catch(() => {});
    return () => { vivo = false; };
  }, []);

  // Mi política vive en la base de datos; si la tabla aún no existe se queda en el dispositivo.
  useEffect(() => {
    let vivo = true;
    (async () => {
      const r = await listarPolitica();
      if (!vivo) return;
      if (!r.ok) { setPolDb(false); return; }
      let items = r.items;
      const locales = cargarPolitica();
      if (!items.length && locales.length) {
        try { items = await migrarPolitica(locales); guardar(LS.politica, []); } catch { if (vivo) setPolDb(false); return; }
      }
      if (vivo) { setPolitica(items); setPolDb(true); }
    })();
    return () => { vivo = false; };
  }, []);
  const [libre, setLibre] = useState('');
  const [leyendo, setLeyendo] = useState(false);
  const [vista, setVista] = useState(null);
  const [vistaCargando, setVistaCargando] = useState(false);
  const [exportando, setExportando] = useState('');
  const [paso, setPaso] = useState(0);
  const [idioma, setIdioma] = useState('pt');
  const [formato, setFormato] = useState('a4');
  const [tipo, setTipo] = useState('pdf');
  const [desbordo, setDesbordo] = useState(false);
  const fileRef = useRef(null);
  const topRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const idA = idAerolinea(f.aerolinea);
  const aero = AEROLINEAS.find((a) => a.id === idA) || AEROLINEAS[3];
  const [rMin, rMax] = rangos[idA] || [aero.min, aero.max];
  const opciones = useMemo(() => valoresMilheiro(rMin, rMax), [rMin, rMax]);
  const milheiro = ajustarMilheiro(f.milheiro);
  const margenPct = f.margen === 'otro' ? Number(f.margenOtro) || 0 : (f.margen === 'manual' ? f.margenUlt : f.margen);
  const fuente = FUENTES.find((x) => x.id === f.fuente) || FUENTES[0];
  const esMillas = fuente.id === 'millas';

  // Costo real: con millas = millas/1000 × milheiro + taxa; con proveedor = su fórmula (Comprarviagem × factor).
  const costoMillas = useMemo(
    () => calcularMilhas({ millas: f.millas, milheiro, taxa: f.taxa, margenPct: 0, redondear: false }),
    [f.millas, milheiro, f.taxa],
  );
  const costoBase = esMillas ? (costoMillas?.costo ?? null) : costoDeFuente({ fuente: fuente.id, precio: f.precios[fuente.id], factor });
  const sugerido = useMemo(() => calcularPrecio({ costo: costoBase, margenPct }), [costoBase, margenPct]);
  const calc = useMemo(() => {
    const r = calcularPrecio({ costo: costoBase, margenPct, precioManual: f.precioManual });
    if (!r) return null;
    return esMillas && costoMillas ? { ...r, costoMillas: costoMillas.costoMillas, costoTaxa: costoMillas.costoTaxa } : r;
  }, [costoBase, margenPct, f.precioManual, esMillas, costoMillas]);

  // Pasos: sin vuelo no hay paso "Valor".
  const pasos = f.sinVuelo ? PASOS_TODOS.filter((x) => x.id !== 'valor') : PASOS_TODOS;
  const pid = pasos[Math.min(paso, pasos.length - 1)].id;

  // Líneas del orçamento: el vuelo (si hay) y cada hotel / servicio / ómnibus, con su costo y precio.
  const totales = useMemo(() => {
    const lineas = [];
    if (!f.sinVuelo) lineas.push({ id: 'vuelo', tipo: 'vuelo', costo: costoBase ?? '', precioManual: f.precioManual });
    f.items.forEach((it) => lineas.push({ id: it.id, tipo: it.tipo, costo: it.costo, precioManual: it.precioManual }));
    return totalesOrcamento({ lineas, margenPct });
  }, [f.sinVuelo, f.items, f.precioManual, costoBase, margenPct]);
  const hayItems = f.items.length > 0;
  const listoExport = totales.completo && totales.precio > 0;

  const notasFinales = useMemo(() => {
    const l = NOTAS.filter((n) => notasSel.has(n.id)).map((n) => (idioma === 'es' ? n.textoEs : n.pt));
    politica.forEach((e) => { if (politicaSel.has(e.id)) l.push(e.texto); });
    libre.split('\n').map((x) => x.trim()).filter(Boolean).forEach((x) => l.push(x));
    return l;
  }, [notasSel, politica, politicaSel, libre, idioma]);

  const datos = useMemo(() => {
    const T = tx(idioma);
    const base = {
      idioma, destinoCiudad: f.destinoCiudad, destinoPais: f.destinoPais, origenCodigo: f.origenCodigo, origenCiudad: f.origenCiudad,
      destinoCodigo: f.destinoCodigo, aerolinea: f.aerolinea, cabina: f.cabina, numeroVuelo: f.numeroVuelo.trim(),
      fechaIda: f.fechaIda, salida: f.salida, llegada: f.llegada, duracion: f.duracion,
      conexiones: f.conexiones === '' ? null : Number(f.conexiones), precio: calc?.precio ?? null, validoHasta: f.validoHasta,
      cliente: f.cliente.trim(), adultos: 1, bagagem: f.bagagem.trim(), notas: notasFinales,
      pagos: PAGOS.filter((x) => f.pagos.includes(x.id)).map((x) => x.id), parcelas: f.parcelas,
    };
    if (!hayItems && !f.sinVuelo) return base;
    // Varias líneas: cada ítem con su detalle y el resumen de precios al final.
    const cap = (t) => `${t.charAt(0)}${t.slice(1).toLowerCase()}`;
    const items = f.items.map((it) => {
      if (it.tipo === 'hotel') {
        return { tipo: 'hotel', nombre: it.nombre.trim(), ciudad: it.ciudad.trim(), entrada: it.entrada, salida: it.salida, noches: nochesEntre(it.entrada, it.salida), huespedes: it.huespedes, habitaciones: it.habitaciones, regimen: it.regimen.trim() };
      }
      if (it.tipo === 'omnibus') {
        return { tipo: 'omnibus', origen: it.origen.trim(), destino: it.destino.trim(), fecha: it.fecha, sale: it.sale, llega: it.llega, empresa: it.empresa.trim() };
      }
      return { tipo: 'servicio', titulo: it.titulo.trim(), detalle: it.detalle.trim() };
    });
    const etiquetas = [];
    if (!f.sinVuelo) etiquetas.push(`${cap(T.vuelo)} ${f.origenCodigo || ''} → ${f.destinoCodigo || ''}`.trim());
    f.items.forEach((it) => etiquetas.push(
      it.tipo === 'hotel' ? (it.nombre.trim() || cap(T.hotel))
        : it.tipo === 'omnibus' ? `${cap(T.bus)} ${[it.origen.trim(), it.destino.trim()].filter(Boolean).join(' → ')}`.trim()
          : (it.titulo.trim() || cap(T.servicio)),
    ));
    return {
      ...base, sinVuelo: f.sinVuelo, items, precio: totales.precio, mostrarPrecioLinea: f.mostrarPrecioLinea,
      resumen: totales.lineas.map((l, i) => ({ etiqueta: etiquetas[i], precio: l.precio })),
    };
  }, [idioma, f, calc, notasFinales, hayItems, totales]);

  // Vista previa (con pequeña espera para no redibujar en cada tecla)
  useEffect(() => {
    if (!listoExport || pid !== 'envio') return undefined;
    let vivo = true;
    setVistaCargando(true);
    const t = setTimeout(async () => {
      try {
        const c = await renderOrcamento(datos, { formato, scale: formato === 'a4' ? 0.7 : 0.6, logoUrl: `${process.env.PUBLIC_URL || ''}/logo-principal.png` });
        if (vivo) { setVista(c.toDataURL('image/jpeg', 0.85)); setDesbordo(!!c.desbordo); }
      } catch { if (vivo) setVista(null); }
      if (vivo) setVistaCargando(false);
    }, 450);
    return () => { vivo = false; clearTimeout(t); };
  }, [datos, listoExport, pid, formato]);

  const setCodigo = (campo, ciudadCampo, v) => {
    const cod = v.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 3);
    setF((p) => {
      const anterior = CIUDADES[p[campo]];
      const auto = !p[ciudadCampo] || p[ciudadCampo] === anterior;
      return { ...p, [campo]: cod, [ciudadCampo]: auto && CIUDADES[cod] ? CIUDADES[cod] : p[ciudadCampo] };
    });
  };

  const elegirAerolinea = (a) => {
    setF((p) => ({ ...p, aerolinea: a.id === 'otra' ? '' : a.label, milheiro: '' }));
  };

  const moverMilheiro = (dir) => {
    const base = milheiro ?? (dir > 0 ? rMin - PASO_MILHEIRO : rMin + PASO_MILHEIRO);
    const nuevo = Math.max(PASO_MILHEIRO, base + dir * PASO_MILHEIRO);
    set('milheiro', String(nuevo));
  };

  const guardarRango = (i, v) => {
    const n = Number(String(v).replace(',', '.'));
    const par = [rMin, rMax]; par[i] = Number.isFinite(n) ? n : par[i];
    const sig = { ...rangos, [idA]: par };
    setRangos(sig); guardar(LS.rangos, sig);
  };

  // Lee la captura con IA y rellena lo que pueda; el resto queda para ti.
  const leerCaptura = async (file) => {
    if (!file) return;
    setLeyendo(true);
    try {
      const { base64, mediaType } = await fileToBase64Jpeg(file);
      const res = await fetch(EXTRACT_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ image: base64, mediaType }),
      });
      const d = await res.json().catch(() => ({}));
      const nuevo = {};
      let leidos = 0;
      const poner = (k, v) => { if (v !== null && v !== undefined && v !== '') { nuevo[k] = v; leidos += 1; } };
      poner('aerolinea', d.airline); poner('cabina', d.cabin);
      poner('fechaIda', d.date); poner('salida', d.departure_time); poner('llegada', d.arrival_time);
      poner('numeroVuelo', d.flight_number); poner('duracion', normalizarDuracion(d.duration));
      poner('conexiones', d.stops === null || d.stops === undefined ? null : String(d.stops));
      if (d.origin_code) { poner('origenCodigo', d.origin_code); if (CIUDADES[d.origin_code]) nuevo.origenCiudad = CIUDADES[d.origin_code]; }
      if (d.destination_code) { poner('destinoCodigo', d.destination_code); if (CIUDADES[d.destination_code]) nuevo.destinoCiudad = CIUDADES[d.destination_code]; }
      // Precio: millas, o reales por adulto. Si la captura no dice de qué proveedor es, se pregunta (no se supone el factor).
      let precios = null; let precioLeido = null;
      if (d.miles !== null && d.miles !== undefined) {
        poner('millas', String(d.miles)); poner('taxa', d.taxa === null || d.taxa === undefined ? null : String(d.taxa));
        nuevo.fuente = 'millas';
      } else if (d.price_adult) {
        leidos += 1;
        if (d.supplier === 'comprarviagem' || d.supplier === 'speedmilhas') {
          nuevo.fuente = d.supplier; precios = { [d.supplier]: String(d.price_adult) };
        } else precioLeido = d.price_adult;
      }
      if (!leidos) toast('No pude leer datos de esa imagen. Llénalos a mano.', 'error');
      else {
        setF((p) => ({
          ...p, ...nuevo, precioManual: '',
          precios: precios ? { ...p.precios, ...precios } : p.precios,
          precioLeido: precioLeido ?? p.precioLeido,
          milheiro: nuevo.aerolinea && idAerolinea(nuevo.aerolinea) !== idAerolinea(p.aerolinea) ? '' : p.milheiro,
        }));
        toast(d.multiple ? `Leí ${leidos} datos del primer vuelo de la lista. Revísalos antes de seguir.` : `Leí ${leidos} datos de la captura. Revísalos antes de seguir.`);
      }
    } catch { toast('No se pudo leer la captura.', 'error'); }
    setLeyendo(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const guardarEntrada = async () => {
    if (!editando) return;
    const titulo = editando.titulo.trim(); const texto = editando.texto.trim();
    if (!titulo || !texto) return;
    try {
      const fila = polDb
        ? await guardarEntradaPolitica({ id: editando.id, titulo, texto, orden: politica.length })
        : { id: editando.id || `p${Date.now()}`, titulo, texto };
      const sig = editando.id ? politica.map((e) => (e.id === fila.id ? fila : e)) : [...politica, fila];
      setPolitica(sig);
      if (!polDb) guardar(LS.politica, sig);
      setPoliticaSel((x) => new Set(x).add(fila.id));
      setEditando(null);
      toast('Guardado en Mi política.');
    } catch { toast('No se pudo guardar. Intenta otra vez.', 'error'); }
  };
  const borrarEntrada = async (e) => {
    if (!window.confirm(`¿Borrar "${e.titulo}" de Mi política?`)) return;
    try {
      if (polDb) await borrarEntradaPolitica(e.id);
      const sig = politica.filter((x) => x.id !== e.id);
      setPolitica(sig);
      if (!polDb) guardar(LS.politica, sig);
      setPoliticaSel((x) => { const n = new Set(x); n.delete(e.id); return n; });
    } catch { toast('No se pudo borrar. Intenta otra vez.', 'error'); }
  };
  const alternar = (setter, id) => setter((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const irPaso = (n) => {
    setPaso(Math.max(0, Math.min(pasos.length - 1, n)));
    topRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };
  const alternarPago = (id) => setF((p) => {
    const sel = p.pagos.includes(id) ? p.pagos.filter((x) => x !== id) : [...p.pagos, id];
    return { ...p, pagos: sel.length ? sel : p.pagos };
  });
  const elegirFuente = (id) => setF((p) => ({ ...p, fuente: id, precioManual: '', margen: typeof p.margen === 'number' || p.margen === 'otro' ? p.margen : p.margenUlt }));
  const usarPrecioLeido = (id) => setF((p) => ({ ...p, fuente: id, precios: { ...p.precios, [id]: String(p.precioLeido) }, precioLeido: null, precioManual: '' }));
  const elegirMargen = (m) => setF((p) => ({ ...p, margen: m, margenUlt: typeof m === 'number' ? m : p.margenUlt, precioManual: '' }));
  const escribirPrecio = (v) => setF((p) => ({ ...p, precioManual: v, margen: v === '' ? p.margenUlt : 'manual' }));
  const agregarItem = (tipo) => setF((p) => ({
    ...p,
    items: [...p.items, {
      id: `i${Date.now()}${Math.random().toString(36).slice(2, 6)}`, tipo, nombre: '', ciudad: p.destinoCiudad || '', entrada: '', salida: '', huespedes: '2', habitaciones: '1', regimen: '',
      titulo: '', detalle: '', origen: '', destino: '', fecha: '', sale: '', llega: '', empresa: '', costo: '', precioManual: '',
    }],
  }));
  const cambiarItem = (id, patch) => setF((p) => ({ ...p, items: p.items.map((it) => (it.id === id ? { ...it, ...patch } : it)) }));
  const quitarItem = (id) => setF((p) => ({ ...p, items: p.items.filter((it) => it.id !== id) }));
  const elegirValidez = (dias) => set('validoHasta', hoyMas(dias));

  const faltan = (f.sinVuelo
    ? [!f.destinoCiudad.trim() && 'destino']
    : [!f.destinoCodigo && 'destino', !f.fechaIda && 'fecha', !f.salida && 'hora de salida', !f.llegada && 'hora de llegada']
  ).filter(Boolean);

  const elegirFormato = (id) => { setFormato(id); setTipo(TIPO_POR_FORMATO[id]); };
  // Al cambiar de idioma, el equipaje se traduce solo si sigue siendo el texto de fábrica.
  const cambiarIdioma = (id) => {
    setIdioma(id);
    setF((p) => (p.bagagem === BAGAGEM.pt || p.bagagem === BAGAGEM.es ? { ...p, bagagem: BAGAGEM[id] } : p));
  };

  // modo: 'bajar' descarga el archivo; 'compartir' abre el menú de compartir del teléfono.
  const exportar = async (modo) => {
    if (!listoExport) return;
    setExportando(modo);
    try {
      const fm = FORMATOS[formato];
      const t = TIPOS.find((x) => x.id === tipo);
      const canvas = await renderOrcamento(datos, { formato, scale: fm.escala, logoUrl: `${process.env.PUBLIC_URL || ''}/logo-principal.png` });
      const blob = tipo === 'pdf' ? await canvasToPdfBlob(canvas)
        : tipo === 'png' ? await canvasToBlob(canvas, 1, 'image/png') : await canvasToBlob(canvas, 0.93);
      const partes = ['orcamento', f.ref.trim(), f.cliente.trim(), f.destinoCodigo || f.destinoCiudad.trim(), formato].filter(Boolean).join('-').replace(/\s+/g, '-');
      const nombre = `${partes}.${t.ext}`;
      const r = await entregarArchivo({ blob, nombre, mime: t.mime, modo });
      if (r === 'cancelado') { setExportando(''); return; }
      if (modo === 'compartir' && r === 'descargado') toast('Este dispositivo no permite compartir: se descargó el archivo.');
      toast(`${t.label} listo.`);
    } catch { toast('No se pudo generar el orçamento.', 'error'); }
    setExportando('');
  };

  const nuevoOrcamento = () => {
    setF((p) => ({ ...INICIAL(), bagagem: p.bagagem }));
    setLibre(''); setPaso(0);
    toast('Listo para un orçamento nuevo.');
  };

  const itemsCompletos = totales.lineas.every((l) => l.completa);
  const paginaCompleta = pid === 'envio';
  const puedeAvanzar = pid === 'valor' ? !!calc : pid === 'items' ? (f.sinVuelo ? hayItems : true) && itemsCompletos : true;

  return (
    <div ref={topRef} style={{ scrollMarginTop: 12 }}>
      <style>{CSS}</style>

      {/* Cabecera de pasos */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--white)', margin: 0 }}>{pasos[paso].label}</h3>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>{paso + 1} de {pasos.length}</span>
        </div>
        <div role="progressbar" aria-valuemin={1} aria-valuemax={pasos.length} aria-valuenow={paso + 1} style={{ height: 6, borderRadius: 3, background: 'var(--navy-border)', overflow: 'hidden' }}>
          <div style={{ width: `${((paso + 1) / pasos.length) * 100}%`, height: '100%', background: 'var(--orange)', transition: 'width .25s ease' }} />
        </div>
      </div>

      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 18, padding: 16, marginBottom: 14 }}>

      {pid === 'vuelo' && (
        <div>
          <Interruptor activo={f.sinVuelo} onChange={(v) => setF((p) => ({ ...p, sinVuelo: v }))}>Este orçamento no lleva vuelo (solo hotel, servicios u ómnibus)</Interruptor>
          {!f.sinVuelo && <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 14px' }}>Sube la captura y leo los datos; todo se puede corregir.</p>}

        {f.sinVuelo ? (
          <div>
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 12px' }}>Pon el destino que sale como título de la imagen. El hotel y los servicios se agregan en el paso "Hotel y servicios".</p>
            <Fila cols={2}>
              <Campo label="Destino (título)"><Texto value={f.destinoCiudad} onChange={(v) => set('destinoCiudad', v)} placeholder="Florianópolis" /></Campo>
              <Campo label="País"><Texto value={f.destinoPais} onChange={(v) => set('destinoPais', v)} placeholder="Brasil" /></Campo>
            </Fila>
          </div>
        ) : (
          <div>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => leerCaptura(e.target.files?.[0])} />
        <button type="button" className="ml-drop" disabled={leyendo} onClick={() => fileRef.current?.click()} style={{ marginBottom: 14 }}>
          {leyendo ? <span className="ml-spin" role="status" aria-label="Leyendo captura" /> : <span style={{ color: 'var(--orange)' }}><IcCamara /></span>}
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)' }}>{leyendo ? 'Leyendo la captura…' : 'Subir captura de la oferta'}</span>
          <span style={{ fontSize: 12 }}>Buscamilhas, grupo de alertas… o llena abajo a mano</span>
        </button>

        <Fila cols={2}>
          <Campo label="Origen (código)"><Texto value={f.origenCodigo} onChange={(v) => setCodigo('origenCodigo', 'origenCiudad', v)} placeholder="CWB" maxLength={3} upper autoCapitalize="characters" /></Campo>
          <Campo label="Destino (código)"><Texto value={f.destinoCodigo} onChange={(v) => setCodigo('destinoCodigo', 'destinoCiudad', v)} placeholder="FLN" maxLength={3} upper autoCapitalize="characters" /></Campo>
        </Fila>
        <Fila cols={2}>
          <Campo label="Ciudad de origen"><Texto value={f.origenCiudad} onChange={(v) => set('origenCiudad', v)} placeholder="Curitiba" /></Campo>
          <Campo label="Ciudad de destino"><Texto value={f.destinoCiudad} onChange={(v) => set('destinoCiudad', v)} placeholder="Florianópolis" /></Campo>
        </Fila>
        <Fila cols={2}>
          <Campo label="Fecha de ida"><Texto type="date" value={f.fechaIda} onChange={(v) => set('fechaIda', v)} /></Campo>
          <Campo label="País (en el título)"><Texto value={f.destinoPais} onChange={(v) => set('destinoPais', v)} placeholder="Brasil" /></Campo>
        </Fila>
        <Fila cols={2}>
          <Campo label="Salida"><Texto type="time" value={f.salida} onChange={(v) => set('salida', v)} /></Campo>
          <Campo label="Llegada"><Texto type="time" value={f.llegada} onChange={(v) => set('llegada', v)} /></Campo>
        </Fila>
        <Fila cols={2}>
          <Campo label="Duración"><Texto value={f.duracion} onChange={(v) => set('duracion', v)} placeholder="3h 10min" /></Campo>
          <Campo label="Conexiones"><Texto type="number" inputMode="numeric" min={0} value={f.conexiones} onChange={(v) => set('conexiones', v)} placeholder="0 = directo" /></Campo>
        </Fila>
        <Campo label="Número de vuelo (opcional)" style={{ marginBottom: 10 }}>
          <Texto value={f.numeroVuelo} onChange={(v) => set('numeroVuelo', v.toUpperCase())} placeholder="LA 3456" maxLength={10} />
        </Campo>
        <Campo label="Compañía" style={{ marginBottom: 8 }}>
          <div className="ml-row" role="group" aria-label="Compañía aérea">
            {AEROLINEAS.map((a) => (
              <button key={a.id} type="button" className="ml-chip" aria-pressed={idA === a.id} onClick={() => elegirAerolinea(a)}>{a.label}</button>
            ))}
          </div>
        </Campo>
        {idA === 'otra' && <Texto value={f.aerolinea} onChange={(v) => set('aerolinea', v)} placeholder="Nombre de la compañía" />}
                </div>
        )}
        </div>
      )}

      {pid === 'valor' && (
        <div>
          <label style={labelStyle}>¿De dónde sale el precio?</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 4 }} role="group" aria-label="Fuente del precio">
            {FUENTES.map((x) => (
              <button key={x.id} type="button" className="ml-chip" aria-pressed={f.fuente === x.id} onClick={() => elegirFuente(x.id)}>{x.label}</button>
            ))}
          </div>

          {f.precioLeido && esMillas && (
            <div style={{ margin: '4px 0 14px', padding: 12, borderRadius: 14, border: '1px solid var(--orange)', background: 'rgba(255,107,0,0.08)' }}>
              <p style={{ fontSize: 13, color: 'var(--white)', margin: '0 0 8px', lineHeight: 1.4 }}>En la captura vi <strong>R$ {fmtBRL(f.precioLeido)}</strong> por adulto. ¿De dónde es ese precio?</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {FUENTES.filter((x) => x.id !== 'millas').map((x) => (
                  <button key={x.id} type="button" className="ml-chip" onClick={() => usarPrecioLeido(x.id)} style={{ fontSize: 13 }}>{x.label}</button>
                ))}
              </div>
            </div>
          )}

          {esMillas ? (
            <div style={{ marginTop: 14 }}>
        <Fila cols={2}>
          <Campo label="Millas por pasajero"><NumInput value={f.millas} onChange={(v) => set('millas', v)} placeholder="23528" /></Campo>
          <Campo label="Taxa de embarque (R$)"><NumInput value={f.taxa} onChange={(v) => set('taxa', v)} placeholder="48,63" /></Campo>
        </Fila>

        <label style={labelStyle}>Milheiro de {aero.label} (R$ por 1.000 millas)</label>
        <div className="ml-row" role="group" aria-label="Milheiro">
          {opciones.map((v) => (
            <button key={v} type="button" className="ml-chip" aria-pressed={milheiro === v} onClick={() => set('milheiro', String(v))}>{fmtBRL(v)}</button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
          <button type="button" className="ml-btn" aria-label="Bajar 0,25" onClick={() => moverMilheiro(-1)} style={{ width: 48, padding: 0, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--white)' }}><IcMenos /></button>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <p style={{ fontSize: 26, fontWeight: 800, color: milheiro ? 'var(--white)' : 'var(--muted)', margin: 0, lineHeight: 1.1 }}>{milheiro ? `R$ ${fmtBRL(milheiro)}` : 'Elige uno'}</p>
            <p style={{ fontSize: 11, color: 'var(--muted)', margin: 0 }}>por cada 1.000 millas</p>
          </div>
          <button type="button" className="ml-btn" aria-label="Subir 0,25" onClick={() => moverMilheiro(1)} style={{ width: 48, padding: 0, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--white)' }}><IcMas /></button>
        </div>
        <button type="button" onClick={() => setEditRango((v) => !v)} style={{ background: 'none', color: 'var(--muted)', fontSize: 12, textDecoration: 'underline', minHeight: 36, marginTop: 6 }}>
          {editRango ? 'Cerrar' : `Cambiar el rango de ${aero.label} (${fmtBRL(rMin)} a ${fmtBRL(rMax)})`}
        </button>
        {editRango && (
          <Fila cols={2}>
            <Campo label="Desde"><NumInput value={rMin} onChange={(v) => guardarRango(0, v)} /></Campo>
            <Campo label="Hasta"><NumInput value={rMax} onChange={(v) => guardarRango(1, v)} /></Campo>
          </Fila>
        )}
            </div>
          ) : (
            <div style={{ marginTop: 14 }}>
              <Campo label={`${fuente.campo} (R$)`}>
                <NumInput value={f.precios[fuente.id]} onChange={(v) => setF((p) => ({ ...p, precios: { ...p.precios, [fuente.id]: v }, precioManual: '' }))} placeholder="2774,76" />
              </Campo>
              <p style={{ fontSize: 12, color: 'var(--muted)', margin: '8px 0 0', lineHeight: 1.45 }}>
                {fuente.ayuda}
                {fuente.usaFactor && costoBase !== null && <> Tu costo real = lista × {String(factor).replace('.', ',')} (factor de tus Ajustes del Cotizador) = <strong>R$ {fmtBRL(costoBase)}</strong>.</>}
              </p>
            </div>
          )}

          <div style={{ height: 1, background: 'var(--navy-border)', margin: '18px 0' }} />
          <h4 style={{ fontSize: 14, fontWeight: 800, color: 'var(--white)', margin: '0 0 4px' }}>Tu precio</h4>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 12px' }}>Los botones te sugieren un precio sobre el costo; puedes escribir el que quieras.</p>

          <div className="ml-row" role="group" aria-label="Margen de ganancia">
            {MARGENES.map((m) => (
              <button key={m} type="button" className="ml-chip" aria-pressed={f.margen === m} onClick={() => elegirMargen(m)} style={{ flex: 1, justifyContent: 'center' }}>{m}%</button>
            ))}
            <button type="button" className="ml-chip" aria-pressed={f.margen === 'otro'} onClick={() => elegirMargen('otro')} style={{ flex: 1, justifyContent: 'center' }}>Otro</button>
          </div>
          {f.margen === 'otro' && (
            <div style={{ marginTop: 8, maxWidth: 160 }}><Campo label="Margen (%)"><NumInput value={f.margenOtro} onChange={(v) => set('margenOtro', v)} placeholder="12" /></Campo></div>
          )}
          <div style={{ marginTop: 10 }}>
            <Campo label="Precio al cliente (R$)">
              <NumInput value={f.precioManual !== '' ? f.precioManual : (sugerido ? String(sugerido.precio) : '')} onChange={escribirPrecio} placeholder="Lo que cobrarás" />
            </Campo>
          </div>

          <div aria-live="polite" style={{ marginTop: 14, borderRadius: 16, overflow: 'hidden', border: '1px solid var(--navy-border)' }}>
            {calc ? (
              <>
                <div style={{ padding: '16px 16px 14px', background: 'linear-gradient(135deg,#06284A,#0B4C8C)', color: '#fff' }}>
                  <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, opacity: 0.75, margin: 0 }}>PRECIO AL CLIENTE</p>
                  <p style={{ fontSize: 40, fontWeight: 800, margin: '2px 0 0', lineHeight: 1.1 }}><span style={{ fontSize: 20, color: '#FF9A4D', marginRight: 6 }}>R$</span>{fmtBRL(calc.precio)}</p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: 'var(--navy-card)' }}>
                  <div style={{ padding: 14, borderRight: '1px solid var(--navy-border)' }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', margin: 0, letterSpacing: 0.5 }}>TU COSTO REAL</p>
                    <p style={{ fontSize: 22, fontWeight: 800, color: 'var(--white)', margin: '2px 0 0' }}>R$ {fmtBRL(calc.costo)}</p>
                    <p style={{ fontSize: 11, color: 'var(--muted)', margin: '2px 0 0' }}>
                      {esMillas ? `millas ${fmtBRL(calc.costoMillas)} + taxa ${fmtBRL(calc.costoTaxa)}` : fuente.usaFactor ? `lista ${fmtBRL(f.precios[fuente.id])} × ${String(factor).replace('.', ',')}` : fuente.campo}
                    </p>
                  </div>
                  <div style={{ padding: 14 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', margin: 0, letterSpacing: 0.5 }}>TU GANANCIA</p>
                    <p style={{ fontSize: 22, fontWeight: 800, color: calc.ganancia < 0 ? 'var(--error)' : 'var(--success)', margin: '2px 0 0' }}>R$ {fmtBRL(calc.ganancia)}</p>
                    <p style={{ fontSize: 11, color: calc.ganancia < 0 ? 'var(--error)' : 'var(--muted)', margin: '2px 0 0' }}>
                      {calc.ganancia < 0 ? 'Estás perdiendo dinero' : `${fmtBRL(calc.gananciaPct, 1)}% sobre el costo`}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <p style={{ padding: 16, margin: 0, fontSize: 13, color: 'var(--muted)', background: 'var(--navy-card)' }}>
                {esMillas ? 'Pon las millas y elige el milheiro para ver el precio.' : `Pon el ${fuente.campo.toLowerCase()} para ver tu costo y tu precio.`}
              </p>
            )}
          </div>
          {f.precioManual === '' && <p style={{ fontSize: 11, color: 'var(--muted)', margin: '8px 0 0' }}>El precio sugerido se redondea hacia arriba al real entero.</p>}
        </div>
      )}

      {pid === 'items' && (
        <div>
          <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 12px', lineHeight: 1.45 }}>
            Agrega lo que más lleva este orçamento. Cada cosa sale con su detalle y, al final, todo con su precio. Si solo es el vuelo, sigue con Continuar.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }} role="group" aria-label="Agregar al orçamento">
            {TIPOS_ITEM.map((t) => (
              <button key={t.id} type="button" className="ml-chip" onClick={() => agregarItem(t.id)}><IcMas />{t.label}</button>
            ))}
          </div>

          <label style={labelStyle}>Margen sugerido para los precios</label>
          <div className="ml-row" role="group" aria-label="Margen de ganancia">
            {MARGENES.map((m) => (
              <button key={m} type="button" className="ml-chip" aria-pressed={f.margen === m} onClick={() => elegirMargen(m)} style={{ flex: 1, justifyContent: 'center' }}>{m}%</button>
            ))}
            <button type="button" className="ml-chip" aria-pressed={f.margen === 'otro'} onClick={() => elegirMargen('otro')} style={{ flex: 1, justifyContent: 'center' }}>Otro</button>
          </div>
          {f.margen === 'otro' && (
            <div style={{ marginBottom: 8, maxWidth: 160 }}><Campo label="Margen (%)"><NumInput value={f.margenOtro} onChange={(v) => set('margenOtro', v)} placeholder="12" /></Campo></div>
          )}
          <p style={{ fontSize: 11, color: 'var(--muted)', margin: '0 0 14px' }}>Si pones costo y no escribes precio, se sugiere costo + margen (redondeado hacia arriba). El del vuelo lo ajustas en el paso Valor.</p>

          {f.items.map((it, i) => (
            <TarjetaItem key={it.id} it={it} linea={totales.lineas[(f.sinVuelo ? 0 : 1) + i]} margenPct={margenPct} onChange={(patch) => cambiarItem(it.id, patch)} onQuitar={() => quitarItem(it.id)} />
          ))}

          {(hayItems || f.sinVuelo) && (
            <div style={{ marginTop: 6 }}>
              <ResumenTotales totales={totales} f={f} />
              <div style={{ marginTop: 12 }}>
                <Interruptor activo={f.mostrarPrecioLinea} onChange={(v) => set('mostrarPrecioLinea', v)}>Mostrar el precio de cada línea en la imagen (si lo apagas, solo sale el total)</Interruptor>
              </div>
            </div>
          )}
          {!itemsCompletos && hayItems && <p style={{ fontSize: 12, color: 'var(--gold)', margin: '4px 0 0' }}>Falta el costo o el precio en alguna línea.</p>}
        </div>
      )}

      {pid === 'cliente' && (
        <div>
          <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 14px' }}>Todo opcional. El nombre, si lo pones, sale en la arte.</p>
          <Campo label="Nombre del cliente" style={{ marginBottom: 12 }}><Texto value={f.cliente} onChange={(v) => set('cliente', v)} placeholder="Ej.: João da Silva" /></Campo>
          <Campo label="Equipaje (sale en la arte)" style={{ marginBottom: 12 }}><Texto value={f.bagagem} onChange={(v) => set('bagagem', v)} placeholder="Mochila + mão 12 kg" /></Campo>
          <label style={labelStyle}>Validez del orçamento</label>
          <div className="ml-row" role="group" aria-label="Días de validez">
            {DIAS_VALIDEZ.map((n) => (
              <button key={n} type="button" className="ml-chip" aria-pressed={f.validoHasta === hoyMas(n)} onClick={() => elegirValidez(n)} style={{ flex: 1, justifyContent: 'center' }}>{n} {n === 1 ? 'día' : 'días'}</button>
            ))}
          </div>
          <Texto type="date" value={f.validoHasta} onChange={(v) => set('validoHasta', v)} />
          <p style={{ fontSize: 11, color: 'var(--muted)', margin: '6px 0 14px' }}>Los precios de las millas cambian rápido: 3 días es un punto de partida prudente.</p>
          <Campo label="Referencia interna (no sale en la arte)"><Texto value={f.ref} onChange={(v) => set('ref', v)} placeholder="Ej.: Yaima-FLN. Solo nombra el archivo." /></Campo>
        </div>
      )}

      {pid === 'obs' && (
        <div>
          <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 14px' }}>Lo que marques sale en la arte como "Observações".</p>

          {/* Mi política */}
          <div style={{ border: '1px solid var(--navy-border)', borderRadius: 16, padding: 14, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
              <h4 style={{ fontSize: 15, fontWeight: 800, color: 'var(--white)', margin: 0 }}>Mi política</h4>
              {!editando && (
                <button type="button" className="ml-chip" onClick={() => setEditando({ id: null, titulo: '', texto: '' })}><IcMas />Agregar</button>
              )}
            </div>
            <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 12px', lineHeight: 1.45 }}>
              Tus reglas con el cliente: cancelación, cambios, devolución… Escríbelas una vez, con las condiciones reales que te da tu balcón, y márcalas cuando las necesites. Escríbelas en el idioma que verá el cliente.
            </p>

            {politica.length === 0 && !editando && (
              <div>
                <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 8px' }}>Aún no tienes ninguna. Toca un tema para empezar:</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {TITULOS_POLITICA.map((t) => (
                    <button key={t} type="button" className="ml-chip" onClick={() => setEditando({ id: null, titulo: t, texto: '' })} style={{ fontSize: 13, borderStyle: 'dashed' }}>{t}</button>
                  ))}
                </div>
              </div>
            )}

            {politica.map((e) => {
              const on = politicaSel.has(e.id);
              return (
                <div key={e.id} style={{ display: 'flex', gap: 6, alignItems: 'stretch', marginBottom: 8 }}>
                  <button type="button" aria-pressed={on} onClick={() => alternar(setPoliticaSel, e.id)} style={{
                    flex: 1, minHeight: 56, textAlign: 'left', padding: '8px 12px', borderRadius: 12, cursor: 'pointer',
                    background: on ? 'rgba(255,107,0,0.10)' : 'var(--navy-card)', border: `1.5px solid ${on ? 'var(--orange)' : 'var(--navy-border)'}`, color: 'var(--white)',
                  }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: on ? 'var(--orange)' : 'var(--white)' }}>{e.titulo}</span>
                    <span style={{ fontSize: 12, color: 'var(--muted)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{e.texto}</span>
                  </button>
                  <button type="button" aria-label={`Editar ${e.titulo}`} onClick={() => setEditando({ ...e })} style={{ width: 44, borderRadius: 12, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><IcLapiz /></button>
                  <button type="button" aria-label={`Borrar ${e.titulo}`} onClick={() => borrarEntrada(e)} style={{ width: 44, borderRadius: 12, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><IcX /></button>
                </div>
              );
            })}

            {editando && (
              <div style={{ marginTop: 8, padding: 12, borderRadius: 14, background: 'var(--navy-card)', border: '1px solid var(--orange)' }}>
                <Campo label="Tema" style={{ marginBottom: 10 }}>
                  <Texto value={editando.titulo} maxLength={40} onChange={(v) => setEditando((x) => ({ ...x, titulo: v }))} placeholder="Ej.: Cancelación" />
                </Campo>
                <Campo label="Lo que le dices al cliente">
                  <textarea value={editando.texto} maxLength={300} rows={4} onChange={(e) => setEditando((x) => ({ ...x, texto: e.target.value }))} placeholder="Escribe la regla tal como se la darías al cliente" style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.4 }} />
                </Campo>
                <p style={{ fontSize: 11, color: 'var(--muted)', margin: '4px 0 10px', textAlign: 'right' }}>{editando.texto.length}/300</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <button type="button" className="ml-btn btn-primary" disabled={!editando.titulo.trim() || !editando.texto.trim()} onClick={guardarEntrada} style={{ width: 'auto', minHeight: 44 }}>Guardar</button>
                  <button type="button" className="ml-btn" onClick={() => setEditando(null)} style={{ minHeight: 44, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--white)' }}>Cancelar</button>
                </div>
              </div>
            )}
            <p style={{ fontSize: 11, color: polDb === false ? 'var(--gold)' : 'var(--muted)', margin: '10px 0 0' }}>
              {polDb === true && 'Guardada en tu cuenta: la ves desde cualquier dispositivo.'}
              {polDb === false && 'Por ahora solo en este dispositivo: falta activar el guardado en la nube.'}
              {polDb === null && 'Cargando…'}
            </p>
          </div>

          <label style={labelStyle}>Frases sugeridas</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {NOTAS.map((n) => (
              <button key={n.id} type="button" className="ml-chip" aria-pressed={notasSel.has(n.id)} onClick={() => alternar(setNotasSel, n.id)} style={{ whiteSpace: 'normal', textAlign: 'left', fontSize: 13, padding: '8px 14px' }}>{n.es}</button>
            ))}
          </div>

          <Campo label="Texto solo para este orçamento (una línea = un punto)">
            <textarea value={libre} maxLength={LIMITE_OBS} onChange={(e) => setLibre(e.target.value)} rows={3} placeholder="Condiciones especiales, plazos acordados…" style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.4 }} />
          </Campo>
          <p style={{ fontSize: 11, color: 'var(--muted)', margin: '4px 0 0', textAlign: 'right' }}>{libre.length}/{LIMITE_OBS}</p>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '10px 0 0', padding: 12, border: '1px solid var(--navy-border)', borderRadius: 12, lineHeight: 1.45 }}>
            Esto se ve en la arte del cliente: no escribas CPF, número de tarjeta ni documentos.
          </p>
        </div>
      )}

      {pid === 'pago' && (
        <div>
          <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 12px' }}>Marca todo lo que aceptas. Sale en la arte.</p>
          <div style={{ display: 'grid', gap: 8 }} role="group" aria-label="Formas de pago">
            {PAGOS.map((x) => {
              const on = f.pagos.includes(x.id);
              return (
                <button key={x.id} type="button" aria-pressed={on} onClick={() => alternarPago(x.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', minHeight: 60, padding: '10px 14px', borderRadius: 14,
                  background: on ? 'rgba(255,107,0,0.10)' : 'var(--navy-card)', border: `1.5px solid ${on ? 'var(--orange)' : 'var(--navy-border)'}`, color: 'var(--white)', cursor: 'pointer',
                }}>
                  <span style={{ flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: on ? 'var(--orange)' : 'var(--white)' }}>{x.es}</span>
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)' }}>{x.sub}</span>
                  </span>
                  <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: 13, border: `2px solid ${on ? 'var(--orange)' : 'var(--navy-border)'}`, background: on ? 'var(--orange)' : 'transparent', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {on && <Svg size={16}><path d="M5 12.5 10 17.5 19 7" /></Svg>}
                  </span>
                </button>
              );
            })}
          </div>
          {f.pagos.includes('Cartão de crédito') && (
            <div style={{ marginTop: 14 }}>
              <label style={labelStyle} htmlFor="ml-parcelas">Cuotas en la tarjeta</label>
              <select id="ml-parcelas" value={f.parcelas} onChange={(e) => set('parcelas', Number(e.target.value))} style={{ ...inputStyle, minHeight: 48 }}>
                <option value={1}>Al contado (la arte no muestra cuotas)</option>
                {Array.from({ length: 11 }, (_, i) => i + 2).map((n) => <option key={n} value={n}>Hasta {n}x</option>)}
              </select>
            </div>
          )}
        </div>
      )}

      {pid === 'envio' && (
        <div>
          <ExportarArte
            idioma={idioma} onIdioma={cambiarIdioma} formato={formato} onFormato={elegirFormato} tipo={tipo} onTipo={setTipo}
            vista={vista} cargando={vistaCargando} listo={listoExport} exportando={exportando} onExportar={exportar}
            textoVacio={listoExport ? 'Preparando vista previa…' : 'Falta el valor o algún precio: vuelve a los pasos anteriores.'}
            aviso={(
              <>
                {desbordo && (
                  <p style={{ fontSize: 12, color: 'var(--gold)', margin: '10px 0 0', lineHeight: 1.4 }}>
                    Tus observaciones no caben completas en {FORMATOS[formato].label} y se recortaron. Acórtalas o usa Story, que admite más texto.
                  </p>
                )}
                {listoExport && faltan.length > 0 && <p style={{ fontSize: 12, color: 'var(--gold)', margin: '10px 0 0' }}>Aún falta: {faltan.join(', ')}.</p>}
              </>
            )}
          />
          <button type="button" className="ml-btn" onClick={nuevoOrcamento} style={{ width: '100%', marginTop: 10, minHeight: 44, background: 'transparent', color: 'var(--muted)', border: '1px solid var(--navy-border)', fontSize: 13 }}>Empezar un orçamento nuevo</button>
        </div>
      )}
      </div>

      {/* Navegación */}
      <div style={{ position: 'sticky', bottom: 0, padding: '10px 0 8px', background: 'var(--bg)', display: 'grid', gap: 8 }}>
        {!paginaCompleta && (
          <button type="button" className="ml-btn btn-primary" disabled={!puedeAvanzar} onClick={() => irPaso(paso + 1)} style={{ width: '100%' }}>
            Continuar <Svg><path d="M5 12h14M13 6l6 6-6 6" /></Svg>
          </button>
        )}
        {paso > 0 && (
          <button type="button" className="ml-btn" onClick={() => irPaso(paso - 1)} style={{ width: '100%', background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--white)' }}>
            <Svg><path d="M19 12H5M11 6l-6 6 6 6" /></Svg> Volver
          </button>
        )}
        {pid === 'items' && !puedeAvanzar && <p style={{ fontSize: 12, color: 'var(--muted)', margin: 0, textAlign: 'center' }}>{f.sinVuelo && !hayItems ? 'Agrega al menos un hotel, servicio u ómnibus.' : 'Pon el costo o el precio de cada línea para continuar.'}</p>}
        {pid === 'valor' && !calc && <p style={{ fontSize: 12, color: 'var(--muted)', margin: 0, textAlign: 'center' }}>{esMillas ? 'Pon las millas y elige el milheiro para continuar.' : 'Pon el precio para continuar.'}</p>}
      </div>
    </div>
  );
}
